import { ISelectItem, Select, TSelectValue } from 'components/select';
import styles from './styles.module.scss';
import InputSQMeter from 'components/input-sq-meter';
import ItemsYmap from 'components/items-ymap';
import Button from 'components/button';
import { useEffect, useState } from 'react';
import { ISelectItemExpended } from 'api/hooks/use-building-by-street';
import {
  IPrimaryFacilityEstimateResponseWithAddress,
  ISecondaryFacilityEstimateResponseWithAddress,
} from 'screens/estimate';
import useComplex from './hooks/useComplex';
import useStreet from './hooks/useStreet';
import useBuildingComplex from './hooks/useBuildingComplex';
import useTypes from './hooks/useTypes';
import useBuildingSecondary from './hooks/useBuilding';
import useArea from './hooks/useArea';
import useGetMarketPrice from './hooks/useGetMarketPrice';
import useReset from './hooks/useReset';
import Loader from 'components/loader';
import clsx from 'clsx';

import { getAddress } from './helpers';
import { useDataEstimateContext } from 'screens/estimate/hooks/useModalContex';
import ModalSupportDeal from '../../modals/modalAnalogs/outOfSystem/modalSupportDeal';
import { MarketTypes } from 'api/types';
import NumberInput from 'components/number-input';

interface IFormEstimateMarketPriceProps {
  isSecondarySwitcher: boolean;
  onCallBackData?: (
    data:
      | ISecondaryFacilityEstimateResponseWithAddress
      | IPrimaryFacilityEstimateResponseWithAddress,
  ) => void;
  resetData?: () => void;
  mode?: 'default' | 'aboutFacilityForm';
}

const FormEstimateMarketPrice = ({
  isSecondarySwitcher,
  onCallBackData,
  resetData,
  mode = 'default',
}: IFormEstimateMarketPriceProps) => {
  const [apartmentNumber, setApartmentNumber] = useState<string>();

  const handleChangeApartmentNumber = (value: string) => {
    setApartmentNumber(value);
  };

  /// modal support ///

  const [isOpenModalSupport, setIsOpenModalSupport] = useState(false);

  const handleChangeModalSupport = (value: boolean) => {
    setIsOpenModalSupport(value);
  };

  ///---Primary---///

  /// complexes
  const { complexes, selectedComplex, isLoadingComplexes, handleSetComplex, handleResetComplex } =
    useComplex(!isSecondarySwitcher);
  /// complexBuildings
  const {
    selectedBuildingPrimary,
    buildingsComplex,
    isLoadingBuildingComplexes,
    handleSetComplexBuilding,
    handleResetBuildingComplex,
  } = useBuildingComplex(selectedComplex);
  /// typesApartment
  const {
    typesApartment,
    selectedTypesApartment,
    minMaxArea,
    isLoadingTypes,
    isLoadingMinMax,
    handleSetTypesApartment,
    handleResetTypesApartment,
  } = useTypes(selectedBuildingPrimary);

  ///---Secondary---///

  /// street
  const { streets, selectedStreet, isLoadingStreets, handleSetStreet, handleResetStreet } =
    useStreet(isSecondarySwitcher);
  /// buildingSecondary
  const {
    selectedBuildingSecondary,
    buildingsSecondary,
    errorBuildings,
    isLoadingBuildings,
    handleSetBuilding,
    handleResetBuilding,
  } = useBuildingSecondary(
    (streets as ISelectItem[]).length ? (streets as ISelectItem[]) : [],
    selectedStreet,
  );

  ///---Common---//

  /// apartmentArea
  const { areaInSqM, errorAreaInSqM, checkValid, handleChangeApartmentArea, handleResetArea } =
    useArea(isSecondarySwitcher, minMaxArea);
  /// getMarketPrice
  const { onSubmit, dataSecondary, dataPrimary, isMutatingSecondary, isMutatingPrimary } =
    useGetMarketPrice(isSecondarySwitcher, handleChangeModalSupport, resetData);

  const data = isSecondarySwitcher ? dataSecondary : dataPrimary;
  const isMutating = isSecondarySwitcher ? isMutatingSecondary : isMutatingPrimary;

  const getMarketPrice = () => {
    onSubmit(
      checkValid,
      selectedBuildingSecondary,
      areaInSqM,
      selectedBuildingPrimary,
      selectedComplex,
      selectedTypesApartment,
    );
  };

  const address = getAddress(
    isSecondarySwitcher,
    streets as ISelectItemExpended[],
    buildingsSecondary as ISelectItemExpended[],
    complexes as ISelectItemExpended[],
    buildingsComplex as ISelectItemExpended[],
    selectedStreet,
    selectedBuildingSecondary,
    selectedComplex,
    selectedBuildingPrimary,
  );

  useEffect(() => {
    if (data) {
      onCallBackData &&
        onCallBackData({
          ...data,
          address,
        });
    }
  }, [data]);

  /// reset
  const { onAllReset } = useReset(
    isSecondarySwitcher,
    {
      handleResetStreet,
      handleResetBuilding,
      handleResetComplex,
      handleResetBuildingComplex,
      handleResetTypesApartment,
      handleResetArea,
      resetData,
    },
    selectedStreet,
    selectedComplex,
    selectedBuildingSecondary,
    selectedBuildingPrimary,
    selectedTypesApartment,
    areaInSqM,
  );

  /// values
  const streetsOrComplex = isSecondarySwitcher ? streets : complexes;
  const selectedStreetOrComplex = isSecondarySwitcher ? selectedStreet : selectedComplex;
  const selectedBuilding = isSecondarySwitcher
    ? selectedBuildingSecondary
    : selectedBuildingPrimary;
  const buildings = isSecondarySwitcher ? buildingsSecondary : buildingsComplex;

  const currentCoords = (function () {
    const key = isSecondarySwitcher ? selectedBuildingSecondary : selectedBuildingPrimary;

    return (buildings as ISelectItemExpended[])?.find((building) => building.key === key)
      ?.coordinates;
  })();

  const typeApartment = (function () {
    if (selectedTypesApartment) {
      return (typesApartment as ISelectItem[]).find((type) => type.key === selectedTypesApartment)
        ?.label as string;
    }
    return undefined;
  })();

  /// useDataEstimateContext
  const { onSetDataModal } = useDataEstimateContext();

  useEffect(() => {
    if (isSecondarySwitcher) {
      if (areaInSqM && address && selectedBuilding) {
        onSetDataModal({
          address,
          areaInSqM,
          buildingId: selectedBuilding,
          complexId: selectedComplex ?? undefined,
          streetId: selectedStreet ?? undefined,
        });
      }
    } else {
      if (areaInSqM && address && typeApartment && selectedTypesApartment && selectedBuilding) {
        onSetDataModal({
          address,
          areaInSqM,
          typeApartment,
          typeApartmentId: selectedTypesApartment,
          buildingId: selectedBuilding,
          complexId: selectedComplex ?? undefined,
          streetId: selectedStreet ?? undefined,
        });
      }
    }

    if (mode === 'aboutFacilityForm') {
      onSetDataModal({
        areaInSqM: areaInSqM ?? undefined,
        typeApartmentId: selectedTypesApartment ?? undefined,
        buildingId: selectedBuilding ?? undefined,
        complexId: selectedComplex ?? undefined,
        streetId: selectedStreet ?? undefined,
        apartmentNumber: apartmentNumber ?? undefined
      });
    }
  }, [
    areaInSqM,
    address,
    typeApartment,
    selectedTypesApartment,
    selectedBuilding,
    selectedComplex,
    selectedStreet,
    apartmentNumber
  ]);

  const onSelectStreetsOrComplex = (value: TSelectValue) => {
    if (isSecondarySwitcher) {
      handleSetStreet(value);
    } else {
      handleSetComplex(value);
    }
  };

  const onSelectBuilding = (value: TSelectValue) => {
    if (isSecondarySwitcher) {
      handleSetBuilding(value);
    } else {
      handleSetComplexBuilding(value);
    }
  };

  const isDisableBuildingSelect = isSecondarySwitcher
    ? !Boolean(selectedStreet)
    : !Boolean(selectedComplex);

  const isAllSelected = !isDisableBuildingSelect && areaInSqM;
  const isLoading =
    isLoadingComplexes ||
    isLoadingBuildingComplexes ||
    isLoadingStreets ||
    isLoadingBuildings ||
    isLoadingTypes ||
    isLoadingMinMax;

  return (
    <div className={styles.form}>
      <div
        className={clsx(
          styles.form__container,
          isLoading && styles.form__container_unclick,
          mode === 'aboutFacilityForm' && styles.form__container_withoutMargin,
        )}
      >
        <div className={styles.form__loader}>{isLoading && <Loader></Loader>}</div>

        <div
          className={clsx(
            styles.form__card,
            isLoading && styles.form__card_loading,
            isSecondarySwitcher && styles.form__card_green,
            mode === 'aboutFacilityForm' && styles.form__card_withoutBorder,
          )}
        >
          {mode === 'default' && (
            <div className={styles.form__title}>
              <h4>Заполните, и мы оценим ваше жилье:</h4>
            </div>
          )}

          <form className={styles.form__form} onSubmit={(e) => e.preventDefault()}>
            <Select
              search
              onSelect={onSelectStreetsOrComplex}
              multiple={false}
              menuArray={
                (streetsOrComplex as ISelectItem[])?.length
                  ? (streetsOrComplex as ISelectItem[])
                  : []
              }
              selected={selectedStreetOrComplex}
              placeholder={isSecondarySwitcher ? 'Укажите адрес' : 'Укажите ЖК'}
              label={isSecondarySwitcher ? 'Адрес' : 'ЖК'}
              isMainPlaceholder
            />
            <Select
              search
              onSelect={onSelectBuilding}
              multiple={false}
              menuArray={
                (buildings as ISelectItemExpended[])?.length
                  ? (buildings as ISelectItemExpended[])
                  : []
              }
              selected={selectedBuilding}
              placeholder={'Укажите дом'}
              label='Дом'
              isMainPlaceholder
              disabled={isDisableBuildingSelect}
            />
            {!isSecondarySwitcher && (
              <Select
                search
                onSelect={handleSetTypesApartment}
                multiple={false}
                menuArray={typesApartment as ISelectItem[]}
                selected={selectedTypesApartment}
                placeholder={'Укажите тип квартиры'}
                label='Тип квартиры'
                isMainPlaceholder
                disabled={!Boolean(selectedBuilding)}
              />
            )}
            <InputSQMeter
              value={areaInSqM}
              onChange={handleChangeApartmentArea}
              error={errorAreaInSqM}
              placeholder={minMaxArea ? `От ${minMaxArea?.min} до ${minMaxArea?.max}` : undefined}
              isDisable={!Boolean(!isSecondarySwitcher ? selectedTypesApartment : true)}
            />
            {mode === 'aboutFacilityForm' && (
              <NumberInput
                onChange={handleChangeApartmentNumber}
                value={apartmentNumber || ''}
                placeholder='Укажите номер квартиры'
                isMainPlaceholder
                label='Номер квартиры'
              />
            )}
          </form>

          <div className={styles.form__map}>
            <ItemsYmap
              items={
                currentCoords
                  ? [
                      {
                        id: 1,
                        coords: [currentCoords.latitude, currentCoords.longitude],
                        selected: true,
                      },
                    ]
                  : []
              }
              isDisableDrag
              zoom={currentCoords ? 15 : 11}
              isSetCenterByFirstItem={currentCoords ? true : false}
            />
          </div>
          {mode === 'default' && (
            <div className={styles.form__button}>
              <Button
                label='Узнать рыночную стоимость'
                onClick={getMarketPrice}
                disabled={isSecondarySwitcher ? !Boolean(isAllSelected) : !Boolean(isAllSelected)}
                loading={isMutating}
                variant={isSecondarySwitcher ? 'default' : 'dark'}
              />
            </div>
          )}
        </div>
      </div>
      {mode === 'default' && (
        <ModalSupportDeal
          isOpen={isOpenModalSupport}
          onAllReset={onAllReset}
          data={{
            areaInSqM: areaInSqM as string,
            typeApartment: typeApartment,
            typeApartmentId: selectedTypesApartment as number,
            address: address,
            buildingId: selectedBuilding as number,
            streetId: selectedStreet as number,
            complexId: selectedComplex as number,
          }}
          onDismiss={handleChangeModalSupport}
          title={isSecondarySwitcher ? 'Заявка на расчет' : 'Поддержка'}
          type='support'
          marketType={isSecondarySwitcher ? MarketTypes.SECONDARY : MarketTypes.PRIMARY}
        />
      )}
    </div>
  );
};
export default FormEstimateMarketPrice;
